import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { CertifiedMapView, ViewToggle } from "@/components/CertifiedMapView";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Search } from "lucide-react";

export default function Browse() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const initialMinScore = Math.max(0, Math.min(100, parseInt(searchParams.get("min_score") || "0", 10) || 0));
  const [minScore, setMinScore] = useState(initialMinScore);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/properties", { params: { q: q || undefined, min_score: minScore } });
        setItems(data);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, minScore]);

  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Certified Fresh</div>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight text-[#0A1128]">Discover verified stays.</h1>
            <p className="mt-3 text-slate-600 max-w-xl">Every property here is monitored for air, humidity, and cleaning quality — with a public certification page.</p>
          </div>
          <div className="text-sm text-slate-500 flex items-center gap-4" data-testid="browse-count">
            <span>{items.length} certified {items.length === 1 ? "stay" : "stays"}</span>
            <ViewToggle view={view} onChange={setView} />
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="browse-search"
              placeholder="Search by city, country, or property name…"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              className="pl-10 h-12 rounded-full bg-white"
            />
          </div>
          <div className="bg-white rounded-full h-12 px-6 border border-slate-200 flex items-center gap-4">
            <span className="text-xs tracking-[0.2em] uppercase text-slate-500 whitespace-nowrap">Min Score</span>
            <Slider value={[minScore]} onValueChange={(v)=>setMinScore(v[0])} min={0} max={100} step={5} className="flex-1" data-testid="browse-min-score" />
            <span className="font-serif text-lg text-[#0A1128] w-8 text-right">{minScore}</span>
          </div>
        </div>

        {loading ? (
          <div className="mt-16 text-center text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="mt-16 text-center text-slate-500" data-testid="browse-empty">No certified properties match your filters.</div>
        ) : view === "map" ? (
          <div className="mt-10">
            <CertifiedMapView items={items} />
          </div>
        ) : (
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="browse-grid">
            {items.map((p, i) => <PropertyCard key={p.id} p={p} idx={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
